import { fromUtcIso, toUtcIso, utcIsoSchema } from '@t4s/domain'

/**
 * Which source commit produced this console bundle, and when it was built.
 *
 * **Why this exists.** `apps/api`'s `GET /health` has carried a `build` object since 2026-08-16 —
 * "a deployment now says which build it is" (`docs/PROJECT-MAP.md`) — for exactly one reason: QA may
 * not read source code (`docs/PROCESS.md` §1), so the running deployment's own answer is the only
 * evidence a report can cite. `apps/platform` had nothing equivalent until this module: a live console
 * session, or a QA report about one, carried no way to name the commit it tested, short of inferring
 * it from GitHub Actions' own run history — see `docs/WORKPLAN.md`,
 * `chore-console-build-identifier`, for the report that found this and drifted on it.
 *
 * **Stamped at build time, not deploy time — a real difference, not cosmetic.** `apps/platform` is
 * static assets with no Worker code of its own (`wrangler.jsonc`'s doc comment), so there is nothing
 * that runs per request to read an injected var the way `apps/api`'s `wrangler deploy --var` values
 * are read at request time. The only place a value can enter this bundle is compiled into the files
 * `vite build` produces — Vite's own `%VITE_BUILD_COMMIT%` / `%VITE_BUILD_TIME%` HTML templating
 * (`index.html`) and its `import.meta.env` exposure (read once, below). That is also why this field is
 * named `time`, never `deployedAt`: it names when the bundle was built, seconds before the same CI job
 * hands it to `wrangler deploy`, not when that upload completed.
 *
 * **One build serves every environment.** `vite.config.ts`'s own doc comment: there is one bundle for
 * pre-prod and prod alike, so the commit here is whichever commit the workflow checked out for the job
 * that produced *this* upload — pre-prod and prod each run their own `pnpm build` from their own
 * checkout, so their consoles carry the same stamp only when they happen to be the same commit.
 *
 * **Absent rather than stale or guessed** — the identical rule `apps/api/src/services/build.ts`
 * states for the same two shapes, restated here rather than imported because these two modules answer
 * to different runtimes (a Worker's `env` bindings vs. a Vite bundle's `import.meta.env`) and sharing
 * one implementation across them would be the coincidence, not the shared concept. A value that does
 * not parse becomes `null`: an identifier nobody can trust is worse than one that is visibly missing.
 * Every local `pnpm dev`, `vite build`, `vite preview` and test run leaves both env vars unset, so
 * `BUILD.commit` and `BUILD.time` are `null` there by design — only the two deploy workflows set them.
 */

export type BuildInfo = {
  /** The full commit this bundle was built from, lower-cased. `null` if absent or malformed. */
  readonly commit: string | null
  /** When the build ran, normalised UTC ISO-8601. `null` if absent or malformed. */
  readonly time: string | null
}

/**
 * Abbreviated (7) through SHA-256 (64) — the identical bound `apps/api/src/services/build.ts` uses,
 * for the identical reason: pinning to 40 would report `null` on a perfectly good build the day this
 * repository's object names change length.
 */
const COMMIT_PATTERN = /^[0-9a-f]{7,64}$/i

function readCommit(raw: string | undefined): string | null {
  const value = raw?.trim() ?? ''
  return COMMIT_PATTERN.test(value) ? value.toLowerCase() : null
}

/**
 * Normalised through `@t4s/domain`, so "a UTC instant" means here exactly what it means at the API:
 * `Z` only, offsets refused rather than converted.
 */
function readTime(raw: string | undefined): string | null {
  const parsed = utcIsoSchema.safeParse(raw?.trim() ?? '')
  return parsed.success ? toUtcIso(fromUtcIso(parsed.data)) : null
}

/** Reads what the build injected, refusing anything it cannot vouch for. */
export function buildInfo(vars: { commit?: string; time?: string }): BuildInfo {
  return {
    commit: readCommit(vars.commit),
    time: readTime(vars.time),
  }
}

/**
 * This bundle's own build info, read once at module load from what Vite baked in at `vite build`
 * time. `index.html`'s `%VITE_BUILD_COMMIT%` / `%VITE_BUILD_TIME%` placeholders carry the identical
 * raw values, unvalidated — that is what the deploy workflow's own verification greps for, so a
 * malformed value stamped by a broken workflow is still visible in the served HTML even though it
 * shows as `—` here.
 */
export const BUILD: BuildInfo = buildInfo({
  commit: import.meta.env.VITE_BUILD_COMMIT,
  time: import.meta.env.VITE_BUILD_TIME,
})
